import AppContext from 'App/AppContext';
import { IPDefaultButton, IPPrimaryButton, IPSeparator, IPText } from 'baseComponents';
import { IPCallout } from 'baseComponents/Callout/IPCallout';
import { IPDirectionalHint } from 'baseComponents/Dialog/IPDialog';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { IPTextField } from 'baseComponents/TextField/IPTextField';
import RichTextEditor, { IRichTextEditor } from 'components/Text/RichTextEditor';
import { globalStackTokensGapSmall } from 'globalStyles';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { clearHtmlValue, htmlToText } from 'utils/html';
import { isEmpty } from 'utils/string';

interface IEditTextFieldCallOut {
  targetId: string;
  onClose: () => void;
  onUpdate: (newText: string) => void;
  isVisible: boolean;
  value?: string;
  isBusy?: boolean;
  maxLenght?: number;
  minLength?: number;
  height?: number;
  width?: number;
  maxHeight?: number;
  required?: boolean;
  readOnly?: boolean;
  isHtml?: boolean;
}

const EditTextFieldCallOut = (props: IEditTextFieldCallOut) => {
  const { t } = useTranslation(['translation']);
  const appContext = useContext(AppContext);
  const editorRef = useRef<IRichTextEditor | null>(null);
  const [newValue, setNewValue] = useState<string>('');
  const [editorVisible, setEditorVisible] = useState(true);

  useEffect(() => {
    const setValue = (value: string | undefined) => {
      if (props.isHtml) {
        const newHtml = clearHtmlValue(value, false, true, true);
        if (newHtml && newHtml !== value) {
          props.onUpdate(newHtml);
        }
        setNewValue(newHtml || '');
      } else {
        setNewValue(value || '');
      }
    };

    if (props.isVisible) {
      setValue(props.value);
      setEditorVisible(true);
      editorRef.current?.focus();
    } else {
      setNewValue('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.isVisible, props.value]);

  // Calculate remaining characters
  const textLen = props.isHtml ? htmlToText(newValue)?.length ?? 0 : newValue.length;
  let remaining = (props.minLength ?? 0) - textLen;
  if (remaining < 0) remaining = 0;

  const getEditor = (): JSX.Element | null => {
    if (!editorVisible) return null; // Hides the editor when needed

    return (
      <IPStack>
        {props.isHtml && (
          <RichTextEditor
            ref={editorRef}
            html={newValue ?? ''}
            directEdit={true}
            maxLength={props.maxLenght}
            disallowFullscreen={true}
            onChange={(value) => {
              if (value === undefined) setNewValue('');
              else setNewValue(value);
            }}
            compactToolbar={true}
            hideRemainingChars={true}
          />
        )}
        {!props.isHtml && (
          <IPTextField
            required={props.required}
            readOnly={props.readOnly}
            autoFocus={!appContext.isMobileView}
            value={newValue ?? ''}
            style={{
              resize: 'vertical',
            }}
            rows={5}
            multiline
            maxLength={props.maxLenght}
            onChange={(event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
              if (newValue === undefined) setNewValue('');
              else setNewValue(newValue);
            }}
          />
        )}
        {props.maxLenght && !props.minLength && (
          <IPText variant="small">{t('translation:General.Text.Length', { len: textLen })}</IPText>
        )}
        {props.minLength && (
          <IPText variant="small">
            {t('translation:General.Text.MinLength', { len: remaining, min: props.minLength })}
          </IPText>
        )}
      </IPStack>
    );
  };

  const closeEditor = () => {
    if (props.isHtml && editorRef.current) {
      editorRef.current.blur();
    }
    setEditorVisible(false);
    // Use a microtask to defer closing
    Promise.resolve().then(() => {
      props.onClose();
    });
  };

  //
  // Main render
  //
  if (!props.targetId) return null;

  return (
    <IPCallout
      hidden={!props.isVisible}
      styles={{
        root: {
          padding: '10px 12px',
          maxHeight: props.maxHeight || 400,
          height: props.height || undefined,
          width: props.width || 500,
          maxWidth: '90vw',
        },
      }}
      target={`#${props.targetId}`}
      directionalHint={IPDirectionalHint.bottomLeftEdge}
      onDismiss={(ev) => {
        closeEditor();
      }}
      preventDismissOnEvent={(ev) => {
        return true;
      }}
    >
      <IPStack tokens={globalStackTokensGapSmall}>
        <IPStack.Item>{getEditor()}</IPStack.Item>
        <IPSeparator />
        <IPStack.Item>
          <IPStack horizontal tokens={globalStackTokensGapSmall} horizontalAlign="end">
            <IPStack.Item>
              <IPPrimaryButton
                onClick={() => {
                  props.onUpdate(newValue);
                  closeEditor();
                }}
                disabled={
                  props.isBusy ||
                  props.readOnly ||
                  (props.required === true && isEmpty(newValue)) ||
                  (props.minLength !== undefined && props.minLength > 0 && newValue.length < props.minLength)
                }
              >
                {t('translation:General.Button.Save')}
              </IPPrimaryButton>
            </IPStack.Item>
            <IPStack.Item>
              <IPDefaultButton
                onClick={() => {
                  closeEditor();
                }}
              >
                {t('translation:General.Button.Cancel')}
              </IPDefaultButton>
            </IPStack.Item>
          </IPStack>
        </IPStack.Item>
      </IPStack>
    </IPCallout>
  );
};

export default EditTextFieldCallOut;
